// «N videos sin versión liviana»: ofrece generar, de una vez, la versión liviana (480p por
// defecto) de los videos que ya existían cuando se estrenó la función, y muestra el avance.
//
// Cómo funciona por dentro (docs/modulo-almacenamiento.md §7.bis):
//   POST /previews/generar  encola los videos y responde de inmediato; el trabajo sigue en el
//                           servidor, de a un video, en la misma cola que la optimización.
//   GET  /previews/estado   avance en memoria; aquí se consulta cada ~5 s SOLO mientras corre y
//                           se deja de consultar al terminar o al salir de la página.
//   409 PREVIEW_DESACTIVADA si la función está apagada en Configuración: como `apiFetch` no deja
//                           ver el código, se lee la configuración para explicar el motivo.
//
// Solo se monta en /almacenamiento (ruta de administrador): ningún otro rol llega acá.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Modal, Progress, Typography } from "antd";
import { ThunderboltOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { configOptionApi } from "../../../services/configOption.api";
import {
  almacenamientoApi,
  type EstadoPreviews,
  type ResumenAlmacenamiento,
} from "../../../services/almacenamiento.api";
import {
  ESTADO_PREVIEWS_VACIO,
  MAX_FALLOS_SONDEO_PREVIEWS,
  SONDEO_PREVIEWS_MS,
  formatPeso,
  hayResultadoPreviews,
  normalizarEstadoPreviews,
  porcentajePreviews,
  procesadosPreviews,
  textoResultadoPreviews,
} from "../almacenamientoUtils";

const { Text } = Typography;

type ErrorPreviews =
  | { tipo: "desactivada"; compresionApagada: boolean }
  | { tipo: "generico" }
  | { tipo: "interrumpido" };

export interface VersionesLivianasProps {
  /** `resumen.videosSinVersionLiviana`; `undefined`/`null` = el servidor no lo manda. */
  sinVersion: ResumenAlmacenamiento["videosSinVersionLiviana"] | null;
  /** Se llama cuando el relleno termina (o no había nada) para recargar el resumen y la lista. */
  onTerminado: () => void;
}

const plural = (n: number, uno: string, varios: string) =>
  `${n.toLocaleString("es-CL")} ${n === 1 ? uno : varios}`;

const VersionesLivianas: React.FC<VersionesLivianasProps> = ({ sinVersion, onTerminado }) => {
  const navigate = useNavigate();
  const cantidad = Math.max(0, Number(sinVersion?.cantidad) || 0);
  const bytes = Math.max(0, Number(sinVersion?.bytes) || 0);

  const [estado, setEstado] = useState<EstadoPreviews>(ESTADO_PREVIEWS_VACIO);
  const [resultado, setResultado] = useState<EstadoPreviews | null>(null);
  const [nadaQueGenerar, setNadaQueGenerar] = useState<{ yaTenian: number; omitidos: number } | null>(
    null,
  );
  const [error, setError] = useState<ErrorPreviews | null>(null);
  const [sondeoCaido, setSondeoCaido] = useState(false);
  const [rondaSondeo, setRondaSondeo] = useState(0);
  const [confirmando, setConfirmando] = useState(false);
  const [iniciando, setIniciando] = useState(false);

  // Referencias para leer lo último desde callbacks de larga vida sin re-suscribir el sondeo.
  const estadoRef = useRef(estado);
  estadoRef.current = estado;
  const onTerminadoRef = useRef(onTerminado);
  onTerminadoRef.current = onTerminado;
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  // Una sola consulta al aparecer la tarjeta: otro administrador (u otra pestaña) pudo dejar un
  // relleno corriendo, y el servidor lo recuerda en memoria.
  const consultoAlMontar = useRef(false);
  const hayQueOfrecer = cantidad > 0;
  useEffect(() => {
    if (!hayQueOfrecer || consultoAlMontar.current) return;
    consultoAlMontar.current = true;
    void almacenamientoApi.estadoPreviews().then((r) => {
      if (!montado.current || !r?.data) return;
      const e = normalizarEstadoPreviews(r.data);
      if (e.enCurso) setEstado(e);
    });
  }, [hayQueOfrecer]);

  // Sondeo: solo mientras hay un relleno corriendo. Se detiene al terminar, al fallar varias veces
  // seguidas y al desmontar.
  useEffect(() => {
    if (!estado.enCurso) return;
    let vivo = true;
    let enVuelo = false;
    let fallos = 0;
    const id = window.setInterval(async () => {
      if (enVuelo) return; // una respuesta lenta no apila consultas
      enVuelo = true;
      const r = await almacenamientoApi.estadoPreviews();
      enVuelo = false;
      if (!vivo) return;
      if (!r?.data) {
        fallos += 1;
        if (fallos >= MAX_FALLOS_SONDEO_PREVIEWS) {
          window.clearInterval(id);
          setSondeoCaido(true);
        }
        return;
      }
      fallos = 0;
      const e = normalizarEstadoPreviews(r.data);
      if (e.enCurso) {
        setEstado(e);
        return;
      }
      window.clearInterval(id);
      if (e.total === 0 && estadoRef.current.total > 0) {
        // El servidor ya no recuerda la corrida: se reinició a mitad de camino.
        setEstado(ESTADO_PREVIEWS_VACIO);
        setError({ tipo: "interrumpido" });
      } else {
        setEstado(e);
        setResultado(e);
      }
      onTerminadoRef.current();
    }, SONDEO_PREVIEWS_MS);
    return () => {
      vivo = false;
      window.clearInterval(id);
    };
  }, [estado.enCurso, rondaSondeo]);

  const reintentarSondeo = useCallback(() => {
    setSondeoCaido(false);
    setRondaSondeo((n) => n + 1);
  }, []);

  /** `apiFetch` devuelve null sin decir por qué: se lee la configuración para explicarlo. */
  const explicarFallo = useCallback(async () => {
    const cfg = await configOptionApi.getVideoOptimization();
    if (!montado.current) return;
    if (cfg?.data && cfg.data.enabled === false) {
      setError({ tipo: "desactivada", compresionApagada: true });
    } else if (cfg?.data && cfg.data.previewEnabled === false) {
      setError({ tipo: "desactivada", compresionApagada: false });
    } else {
      setError({ tipo: "generico" });
    }
  }, []);

  const generar = async () => {
    setConfirmando(false);
    setIniciando(true);
    setError(null);
    setResultado(null);
    setNadaQueGenerar(null);
    setSondeoCaido(false);
    const r = await almacenamientoApi.generarPreviews();
    if (!montado.current) return;
    if (!r?.data) {
      await explicarFallo();
      if (montado.current) setIniciando(false);
      return;
    }
    setIniciando(false);
    const d = r.data;
    if (d.enCurso) {
      // Recién encolado, o ya había una corrida andando (en ese caso la respuesta trae su avance).
      setEstado(
        normalizarEstadoPreviews(d, {
          total: d.encolados,
          pendientes: d.encolados,
          iniciadoEn: new Date().toISOString(),
        }),
      );
      return;
    }
    // Nada que encolar: el resumen estaba desactualizado o los videos ya no lo necesitan.
    setNadaQueGenerar({ yaTenian: d.yaTenian, omitidos: d.omitidos });
    onTerminadoRef.current();
  };

  const enCurso = estado.enCurso;
  const visible = hayQueOfrecer || enCurso || resultado !== null || nadaQueGenerar !== null || error !== null;
  if (!visible) return null;

  const procesados = procesadosPreviews(estado);

  return (
    <section className="alm-livianas" aria-label="Versiones livianas de los videos" data-testid="alm-livianas">
      <div className="alm-livianas-cab">
        <div className="alm-livianas-texto">
          <Text strong className="alm-livianas-titulo">
            {hayQueOfrecer
              ? `${plural(cantidad, "video sin versión liviana", "videos sin versión liviana")} (${formatPeso(bytes)})`
              : "Versiones livianas de los videos"}
          </Text>
          <Text type="secondary" className="alm-livianas-sub">
            Con la versión liviana, ver un video gasta una fracción de los datos. Es una copia
            aparte: los originales no se tocan.
          </Text>
        </div>
        {hayQueOfrecer && (
          <Button
            type="primary"
            className="alm-btn-tactil"
            icon={<ThunderboltOutlined />}
            loading={iniciando}
            disabled={enCurso}
            onClick={() => setConfirmando(true)}
            data-testid="alm-livianas-generar"
          >
            Generar versiones livianas
          </Button>
        )}
      </div>

      {hayQueOfrecer && !enCurso && (
        <Text type="secondary" className="alm-livianas-nota">
          La cifra incluye los videos de 3 MB o más que no tienen versión liviana. Algunos nunca la
          tendrán porque no la necesitan (ya son de baja resolución o la copia no ahorra lo
          suficiente): se omiten solos.
        </Text>
      )}

      {enCurso && (
        <div className="alm-livianas-avance" aria-live="polite" data-testid="alm-livianas-avance">
          <Progress
            percent={Math.round(porcentajePreviews(estado))}
            status="active"
            aria-label="Avance de las versiones livianas"
          />
          <Text>
            Procesando {procesados.toLocaleString("es-CL")} de {estado.total.toLocaleString("es-CL")}{" "}
            videos · {plural(estado.generadas, "creada", "creadas")} ·{" "}
            {plural(estado.omitidas, "omitida", "omitidas")} ·{" "}
            {plural(estado.fallidas, "fallida", "fallidas")}
          </Text>
          <Text type="secondary" className="alm-livianas-nota">
            Sigue en el servidor aunque cambies de página; aquí se actualiza solo cada pocos
            segundos. Una subida nueva espera, como mucho, el video que se esté procesando.
          </Text>
        </div>
      )}

      {sondeoCaido && (
        <Alert
          className="alm-livianas-aviso"
          type="warning"
          showIcon
          message="No se pudo leer el avance"
          description="El proceso sigue en el servidor, pero esta página dejó de consultarlo. Reintenta para volver a ver el avance."
          action={
            <Button onClick={reintentarSondeo} data-testid="alm-livianas-reintentar-sondeo">
              Reintentar
            </Button>
          }
        />
      )}

      {resultado && hayResultadoPreviews(resultado) && (
        <Alert
          className="alm-livianas-aviso"
          type={resultado.fallidas > 0 ? "warning" : "success"}
          showIcon
          closable
          onClose={() => setResultado(null)}
          message="Terminó la generación"
          description={textoResultadoPreviews(resultado)}
          data-testid="alm-livianas-resultado"
        />
      )}

      {nadaQueGenerar && (
        <Alert
          className="alm-livianas-aviso"
          type="info"
          showIcon
          closable
          onClose={() => setNadaQueGenerar(null)}
          message="No había nada por generar en este momento"
          description={`${plural(nadaQueGenerar.yaTenian, "video ya tenía", "videos ya tenían")} versión liviana y ${plural(nadaQueGenerar.omitidos, "se omitió", "se omitieron")} (pesan menos de 3 MB o todavía se están optimizando; su versión liviana sale sola al terminar). Se actualizó la lista.`}
          data-testid="alm-livianas-nada"
        />
      )}

      {error?.tipo === "desactivada" && (
        <Alert
          className="alm-livianas-aviso"
          type="warning"
          showIcon
          closable
          onClose={() => setError(null)}
          message={
            error.compresionApagada
              ? "La compresión de videos está apagada"
              : "La versión liviana está desactivada"
          }
          description={
            error.compresionApagada
              ? "Sin la compresión activa no se generan versiones livianas. Actívala en Configuración, pestaña «Optimización de videos», y vuelve a intentarlo."
              : "Actívala en Configuración, pestaña «Optimización de videos» (opción «Generar la versión liviana»), y vuelve a intentarlo."
          }
          action={
            <Button onClick={() => navigate("/configuracion?tab=videos")} data-testid="alm-livianas-ir-config">
              Ir a Configuración
            </Button>
          }
          data-testid="alm-livianas-desactivada"
        />
      )}

      {error?.tipo === "generico" && (
        <Alert
          className="alm-livianas-aviso"
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          message="No se pudo iniciar la generación"
          description="El servidor no respondió como se esperaba. No se cambió nada: puedes reintentar en unos minutos."
          data-testid="alm-livianas-error"
        />
      )}

      {error?.tipo === "interrumpido" && (
        <Alert
          className="alm-livianas-aviso"
          type="warning"
          showIcon
          closable
          onClose={() => setError(null)}
          message="La generación se interrumpió"
          description="El servidor se reinició mientras trabajaba y perdió la lista pendiente. Lo ya generado se conserva: vuelve a pulsar «Generar versiones livianas» para seguir con los que faltan."
          data-testid="alm-livianas-interrumpido"
        />
      )}

      <Modal
        open={confirmando}
        title="Generar versiones livianas"
        okText="Generar"
        cancelText="Cancelar"
        onOk={() => void generar()}
        onCancel={() => setConfirmando(false)}
        destroyOnClose
        data-testid="alm-livianas-confirmar"
      >
        <p>
          Se crea la versión liviana de los videos que no la tienen (hasta{" "}
          {plural(cantidad, "video", "videos")}, {formatPeso(bytes)} en originales).
        </p>
        <ul style={{ paddingLeft: 18 }}>
          <li>
            Usa CPU del servidor, <b>en segundo plano y de a un video</b> (primero los más
            pesados). La aplicación sigue funcionando; una subida nueva espera, como mucho, el
            video que se esté procesando.
          </li>
          <li>
            <b>No cambia ni borra los originales</b>: la versión liviana es una copia aparte.
          </li>
          <li>
            Los videos que no la necesitan (ya son de baja resolución o la copia no ahorra) se
            omiten.
          </li>
          <li>
            Si el servidor se reinicia, se interrumpe: basta con volver a pulsar para continuar con
            los que falten.
          </li>
        </ul>
      </Modal>
    </section>
  );
};

export default VersionesLivianas;
